import { memo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Heading, useAlert } from '@frontend/design-system';
import { AppointmentTypePaymentFields } from './appointment-type-payment-fields';
import { useAppointmentTypePaymentFields } from './use-appointment-type-payment-fields';

interface Props {
  locationId: string;
}

export const GlobalAppointmentTypesPaymentForm = memo(({ locationId }: Props) => {
  const { t } = useTranslation('schedule');
  const alerts = useAlert();

  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds: [locationId] },
    options: {
      enabled: !!locationId,
    },
  });
  const initialAmount = listLocationSettingsQuery.data?.locationsSettings?.[locationId]?.bookingDeposit;
  const paymentFields = useAppointmentTypePaymentFields(initialAmount);

  const updateLocationSettingsMutation = SchedulerV3.Mutations.useUpdateLocationSettingsMutation({
    options: {
      onError: () => {
        alerts.error(t('Failed to update appointment type payment settings'));
      },
      onSuccess: () => {
        alerts.success(t('Appointment type payment settings updated successfully'));
      },
    },
  });

  const handleSaveClick = () => {
    updateLocationSettingsMutation.mutate({
      locationId,
      bookingDeposit: paymentFields.depositAmount,
    });
  };

  return (
    <section css={containerStyle}>
      <Heading level={2} css={{ marginBottom: theme.spacing(2) }}>
        {t('Appointment Type Payments Settings')}
      </Heading>
      <ContentLoader
        show={updateLocationSettingsMutation.isLoading || listLocationSettingsQuery.isLoading}
        size='medium'
      />
      <AppointmentTypePaymentFields
        getFieldProps={paymentFields.getFieldProps}
        requireDepositSwitchLabel={t('Request below amount for all appointment types')}
      />
      <Button
        css={{ width: 'fit-content', marginTop: theme.spacing(2) }}
        disabled={!paymentFields.isFormComplete || initialAmount === paymentFields.depositAmount}
        loading={updateLocationSettingsMutation.isLoading}
        onClick={handleSaveClick}
      >
        {t('Save Payments Settings')}
      </Button>
    </section>
  );
});

GlobalAppointmentTypesPaymentForm.displayName = 'GlobalAppointmentTypesPaymentForm';

const containerStyle = css({
  position: 'relative',
  maxWidth: 450,
  display: 'flex',
  flexDirection: 'column',
});
